import { Box, Button, Divider, Link, MenuItem, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/service/dataprovider';
import { useNavigate, useParams } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useEffect, useMemo, useState } from 'react';
import Panel from '../../components/Panel';
import LeaseResident from './LeaseResident';
import IncomeVerificationPanel from './IncomeVerificationPanel';
import SigningStatusPanel from './SigningStatusPanel';
import AuditTrailPanel from './AuditTrailPanel';
import ApplicationPreviewPanel from './ApplicationPreviewPanel';
import MoveInConditionPanel from './MoveInConditionPanel';
import { roleBadge } from './roleBadge';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export default function LeaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data, error } = useQuery({
    queryKey: [`leaseDetail/${id}`],
    queryFn: () => api.get(`/v1/admin/lease/${id}`),
  });
  const lease = data?.data?.lease;

  const handleBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) {
      navigate(-1);
      return;
    }
    navigate('/dashboard');
  };

  return (
    <Box>
      <Stack
        flexDirection="row"
        alignItems="center"
        gap={0.5}
        sx={{ cursor: 'pointer', mb: 3, width: 'fit-content', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
        onClick={handleBack}
      >
        <ChevronLeftIcon fontSize="small" />
        <Typography variant="body2" sx={{ color: 'inherit', fontWeight: 500 }}>
          {t('lease.back')}
        </Typography>
      </Stack>
      {lease && (
        <>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <Typography variant="h3">{lease['Unit']}</Typography>
              <Divider orientation="vertical" sx={{ width: '3px', height: '24px' }} />
              <Typography variant="h3" sx={{ color: 'text.secondary' }}>
                {lease.property?.name}
              </Typography>
            </Stack>
            <Link href={`https://resprop.myresman.com/#/Residents/Detail/${lease.id}`} target="_blank" rel="noopener" underline="hover">
              {t('lease.viewLease')}
            </Link>
          </Stack>
          <LeaseDetailContent lease={lease} />
        </>
      )}
      {error && (
        <Typography variant="h4" sx={{ color: 'error.main' }}>
          {error?.response?.data?.message ?? `There is an error for loading lease ${id}. Try again later`}
        </Typography>
      )}
    </Box>
  );
}

function LeaseDetailContent({ lease }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedLeaseLink, setSelectedLeaseLink] = useState(null);
  const [isAddingApplicant, setIsAddingApplicant] = useState(false);
  const [viewIncome, setViewIncome] = useState(false);
  const [viewSigning, setViewSigning] = useState(false);
  const [viewAudit, setViewAudit] = useState(false);
  const [viewMoveIn, setViewMoveIn] = useState(false);
  const [applicantName, setApplicantName] = useState('');
  const [applicantEmail, setApplicantEmail] = useState('');
  const [applicantHouseholdStatus, setApplicantHouseholdStatus] = useState('');

  const householdStatusOptions = useMemo(
    () => ['Spouse/Cohabitant', 'Family Member - Adult', 'Family Member - Minor', 'Roommate', 'Guarantor', 'Live-In Aide'],
    []
  );

  const leaseLinks = useMemo(() => {
    return lease.leaseLinks ?? [];
  }, [lease]);
  const canAddApplicant = lease.Status === 'Pending';
  // Income Verification (ProofUp/LIHTC) only applies to affordable leases.
  const isAffordable = Boolean(lease.isAffordable);
  // The Move-In Condition tab applies to every lease (not affordable-only), so the
  // tab bar always shows. isAffordable/isMultipleLeases/canAddApplicant still gate
  // which OTHER tabs appear.
  const showTabs = true;
  // Income Verification and Audit Trail are both affordable-only. The full
  // application is rendered inline inside each applicant's resident tab (below),
  // not as its own tab.
  const showIncome = isAffordable && viewIncome && !viewAudit && !viewSigning && !viewMoveIn;
  const showSigning = isAffordable && viewSigning && !viewAudit && !viewMoveIn;
  const showAudit = isAffordable && viewAudit && !viewMoveIn;
  // Move-In Condition is available on every lease.
  const showMoveIn = viewMoveIn;
  const isValidEmail = useMemo(() => {
    if (!applicantEmail) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applicantEmail);
  }, [applicantEmail]);

  const addApplicantMutation = useMutation({
    mutationFn: payload => api.post('/v1/admin/add-applicant', payload),
    onSuccess: () => {
      toast.success(t('lease.applicant.successAdded'));
      setApplicantName('');
      setApplicantEmail('');
      setApplicantHouseholdStatus('');
      setIsAddingApplicant(false);
      queryClient.invalidateQueries({ queryKey: [`leaseDetail/${lease.id}`] });
    },
    onError: data => {
      toast.error(data?.response?.data?.err ?? data?.response?.data?.message ?? data?.response?.message ?? t('lease.applicant.errorAdd'));
    },
  });

  useEffect(() => {
    setSelectedLeaseLink(prevSelectedLeaseLink => {
      if (!prevSelectedLeaseLink && leaseLinks?.length > 0) {
        setIsAddingApplicant(false);
        return leaseLinks[0];
      }

      if (prevSelectedLeaseLink) {
        return leaseLinks.find(a => a.id === prevSelectedLeaseLink.id) ?? null;
      }

      return prevSelectedLeaseLink;
    });
  }, [leaseLinks]);

  const handleAddApplicant = () => {
    addApplicantMutation.mutate({
      name: applicantName.trim(),
      email: applicantEmail.trim(),
      householdStatus: applicantHouseholdStatus,
      leaseId: lease.id,
      propertyId: lease.property?.id,
      unitNumber: lease.Unit,
    });
  };

  return (
    <>
      {showTabs && (
        <Box sx={{ mb: 3 }}>
          <Tabs
            sx={{ borderBottom: 1, borderColor: 'divider' }}
            value={
              showMoveIn
                ? 'movein'
                : showAudit
                  ? 'audit'
                  : showSigning
                    ? 'signing'
                    : showIncome
                      ? 'income'
                      : isAddingApplicant
                        ? 'pending'
                        : selectedLeaseLink?.id ?? false
            }
            onChange={(_e, value) => {
              if (value === 'movein') {
                setViewMoveIn(true);
                setViewAudit(false);
                setViewIncome(false);
                setViewSigning(false);
                setIsAddingApplicant(false);
                setSelectedLeaseLink(null);
                return;
              }
              if (value === 'audit') {
                setViewMoveIn(false);
                setViewAudit(true);
                setViewIncome(false);
                setViewSigning(false);
                setIsAddingApplicant(false);
                setSelectedLeaseLink(null);
                return;
              }
              if (value === 'income') {
                setViewMoveIn(false);
                setViewAudit(false);
                setViewIncome(true);
                setViewSigning(false);
                setIsAddingApplicant(false);
                return;
              }
              if (value === 'signing') {
                setViewMoveIn(false);
                setViewAudit(false);
                setViewIncome(false);
                setViewSigning(true);
                setIsAddingApplicant(false);
                return;
              }
              if (value === 'pending') {
                setViewMoveIn(false);
                setViewAudit(false);
                setViewIncome(false);
                setViewSigning(false);
                setIsAddingApplicant(true);
                setSelectedLeaseLink(null);
                return;
              }
              const applicant = leaseLinks.find(applicant => applicant.id === value);
              if (applicant) {
                setViewMoveIn(false);
                setViewAudit(false);
                setViewIncome(false);
                setViewSigning(false);
                setIsAddingApplicant(false);
                setSelectedLeaseLink(applicant);
              }
            }}
          >
            {leaseLinks.map(leaseLink => {
              const { Icon, color, label } = roleBadge(leaseLink);
              return (
                <Tab
                  key={leaseLink.id}
                  value={leaseLink.id}
                  icon={
                    <Tooltip title={label}>
                      <Icon fontSize="small" color={color} />
                    </Tooltip>
                  }
                  iconPosition="start"
                  label={leaseLink.people.FirstName}
                />
              );
            })}
            {canAddApplicant && <Tab value="pending" label={t('lease.applicant.addNewTab')} />}
            {isAffordable && <Tab value="income" label="Income Verification" />}
            {isAffordable && <Tab value="signing" label="Signing Status" />}
            {isAffordable && <Tab value="audit" label="Application Audit Trail" />}
            <Tab value="movein" label="Move-In Condition" />
          </Tabs>
        </Box>
      )}
      {showMoveIn && <MoveInConditionPanel lease={lease} />}
      {!showMoveIn && showAudit && <AuditTrailPanel lease={lease} />}
      {!showMoveIn && !showAudit && showSigning && <SigningStatusPanel lease={lease} />}
      {!showMoveIn && !showAudit && !showSigning && showIncome && <IncomeVerificationPanel lease={lease} />}
      {!showMoveIn && !showAudit && !showSigning && !showIncome && isAddingApplicant && (
        <Panel title={t('lease.applicant.addNewTitle')} sx={{ mb: 3 }}>
          <Stack spacing={2}>
            <TextField size="small" label={t('lease.applicant.name')} value={applicantName} onChange={e => setApplicantName(e.target.value)} fullWidth />
            <TextField
              size="small"
              label={t('lease.applicant.email')}
              type="email"
              value={applicantEmail}
              onChange={e => setApplicantEmail(e.target.value)}
              fullWidth
              error={Boolean(applicantEmail) && !isValidEmail}
              helperText={Boolean(applicantEmail) && !isValidEmail ? t('lease.applicant.invalidEmail') : ''}
            />
            <TextField
              size="small"
              select
              label={t('lease.applicant.householdStatus')}
              value={applicantHouseholdStatus}
              onChange={e => setApplicantHouseholdStatus(e.target.value)}
              fullWidth
            >
              {householdStatusOptions.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsAddingApplicant(false);
                  setApplicantName('');
                  setApplicantEmail('');
                  setApplicantHouseholdStatus('');
                  if (leaseLinks.length > 0) {
                    setSelectedLeaseLink(leaseLinks[0]);
                  }
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleAddApplicant}
                disabled={!applicantName.trim() || !isValidEmail || !applicantHouseholdStatus || addApplicantMutation.isPending}
              >
                {t('lease.applicant.addButton')}
              </Button>
            </Stack>
          </Stack>
        </Panel>
      )}
      {!showMoveIn && !showAudit && !showSigning && !showIncome && selectedLeaseLink && (
        <>
          <LeaseResident lease={lease} leaseLink={selectedLeaseLink} />
          {/* Submitted application — both affordable and conventional flows (panel adapts its layout). */}
          <ApplicationPreviewPanel lease={lease} leaseLink={selectedLeaseLink} />
        </>
      )}
    </>
  );
}
